import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Info,
  Person,
  Work,
  School,
  LocationOn,
  Phone,
  Email,
  LinkedIn,
  Build
} from '@mui/icons-material';

const ProfileCompletionStatus = ({ profileCompletion, candidateData }) => {
  const getProgressColor = (percent) => {
    if (percent >= 80) return 'success';
    if (percent >= 40) return 'warning';
    return 'error';
  };

  const getSectionIcon = (section, isComplete) => {
    const iconMap = {
      'personal': <Person />,
      'education': <School />,
      'experience': <Work />,
      'skills': <Build />
    };
    
    return iconMap[section] || <Info />;
  };

  const getIncompleteItems = () => {
    const items = [];
    
    // Check personal information
    if (!candidateData?.name) items.push('Full name is missing');
    if (!candidateData?.email) items.push('Email address is missing');
    if (!candidateData?.phone) items.push('Phone number is missing');
    if (!candidateData?.currentLocation) items.push('Current location is missing');
    
    // Check education
    if (!candidateData?.education || candidateData.education.length === 0) {
      items.push('No education entries added');
    }
    
    // Check experience
    if (!candidateData?.experience || candidateData.experience.length === 0) {
      items.push('No work experience entries added');
    } else {
      // Check if experience entries have all required fields
      const incompleteExp = candidateData.experience.some(exp => 
        !exp.organization || !exp.designation || !exp.startDate);
      if (incompleteExp) items.push('Some work experiences are missing details');
    }
    
    // Check skills
    if (!candidateData?.skills || candidateData.skills.length === 0) {
      items.push('No skills added to your profile');
    } else if (candidateData.skills.length < 5) {
      items.push('Add more skills to enhance your profile (aim for at least 5)');
    }

    // Check professional info
    if (!candidateData?.currentCompany) items.push('Current company is missing');
    if (!candidateData?.currentRole) items.push('Current role is missing');
    if (!candidateData?.experience_yrs) items.push('Total years of experience is missing');
    
    return items;
  };

  const sections = [
    { 
      name: 'personal', 
      label: 'Personal Information', 
      percent: profileCompletion.personal,
      requiredFields: ['name', 'email', 'phone', 'currentLocation', 'gender', 'DOB']
    },
    { 
      name: 'education', 
      label: 'Education', 
      percent: profileCompletion.education,
      requiredFields: ['education']
    },
    { 
      name: 'experience', 
      label: 'Work Experience', 
      percent: profileCompletion.experience,
      requiredFields: ['experience', 'currentCompany', 'currentRole']
    },
    { 
      name: 'skills', 
      label: 'Skills', 
      percent: profileCompletion.skills,
      requiredFields: ['skills']
    }
  ];

  const incompleteItems = getIncompleteItems();

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ p: 2, mb: 3, position: 'relative', overflow: 'visible' }}>
            <CardContent sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Overall Profile Completion
                </Typography>
                <Chip
                  label={`${profileCompletion.overall}% Complete`}
                  color={getProgressColor(profileCompletion.overall)}
                  sx={{ fontWeight: 'bold', fontSize: '1rem', height: '32px' }}
                />
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={profileCompletion.overall}
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  mb: 2,
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    backgroundColor: getProgressColor(profileCompletion.overall) + '.main'
                  }
                }}
              />
              
              {profileCompletion.overall === 100 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    Your profile is complete! This will help you stand out to recruiters.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Complete your profile to increase your chances of getting noticed by recruiters.
                  {profileCompletion.overall < 40 && " Your profile needs significant improvement."}
                  {profileCompletion.overall >= 40 && profileCompletion.overall < 80 && " You're on the right track!"}
                  {profileCompletion.overall >= 80 && " Almost there! Just a few more details to add."}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Section Progress */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ pb: 1, borderBottom: '1px solid #eee' }}>
              Section Completion Status
            </Typography>
            
            <List>
              {sections.map((section) => (
                <React.Fragment key={section.name}>
                  <ListItem>
                    <ListItemIcon>
                      {getSectionIcon(section.name, section.percent === 100)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body1">{section.label}</Typography>
                          <Typography 
                            variant="body2" 
                            color={getProgressColor(section.percent) + '.main'}
                            sx={{ fontWeight: 'bold' }}
                          >
                            {section.percent}%
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={section.percent}
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              backgroundColor: 'rgba(0,0,0,0.05)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: getProgressColor(section.percent) + '.main'
                              }
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Action Items */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ pb: 1, borderBottom: '1px solid #eee' }}>
              Suggested Improvements
            </Typography>
            
            {incompleteItems.length > 0 ? (
              <List>
                {incompleteItems.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Info color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Your profile is complete!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Great job! Your profile has all the necessary information that recruiters look for.
                </Typography>
              </Box>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => document.getElementById('profile-tab').click()} 
                disabled={incompleteItems.length === 0}
              >
                Update Profile
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Profile Strength Tips */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ pb: 1, borderBottom: '1px solid #eee' }}>
              Tips to Strengthen Your Profile
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Add Relevant Skills
                    </Typography>
                    <Typography variant="body2">
                      List skills that are relevant to your target roles. Be specific and prioritize technical skills that are in demand.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Detailed Work Experience
                    </Typography>
                    <Typography variant="body2">
                      For each role, include accomplishments with measurable results rather than just listing responsibilities.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Professional Summary
                    </Typography>
                    <Typography variant="body2">
                      Write a concise summary highlighting your experience, skills, and career objectives to grab recruiters' attention.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileCompletionStatus;
